export const CommodityCulture = {
    SOYBEAN: 'SOYBEAN',
    CORN: 'CORN',
    SORGHUM: 'SORGHUM',
    COTTON: 'COTTON',
    WHEAT: 'WHEAT',
    COFFEE: 'COFFEE',
    OTHER: 'OTHER',
} as const satisfies Record<string, string>;

export type CommodityCulture = typeof CommodityCulture[keyof typeof CommodityCulture];

export const LegalRegime = {
    AF: 'AF',
    PLEDGE: 'PLEDGE',
    MORTGAGE: 'MORTGAGE',
    FIDUCIARY_ASSIGNMENT: 'FIDUCIARY_ASSIGNMENT',
} as const satisfies Record<string, string>;

export type LegalRegime = typeof LegalRegime[keyof typeof LegalRegime];

export const VerificationType = {
    PLANTING_1: 'PLANTING_1',
    PLANTING_2: 'PLANTING_2',
    HARVEST_SATELLITE: 'HARVEST_SATELLITE',
    HARVEST_IN_LOCO: 'HARVEST_IN_LOCO',
} as const satisfies Record<string, string>;

export type VerificationType = typeof VerificationType[keyof typeof VerificationType];

export const VerificationStatus = {
    PENDING: 'PENDING',
    DONE: 'DONE',
    SKIPPED: 'SKIPPED',
} as const satisfies Record<string, string>;

export type VerificationStatus = typeof VerificationStatus[keyof typeof VerificationStatus];

export const AreaUnit = {
    HECTARE: 'HECTARE',
    SQUARE_METER: 'SQUARE_METER',
} as const satisfies Record<string, string>;

export type AreaUnit = typeof AreaUnit[keyof typeof AreaUnit];

export interface CommodityOwner {
    personId: string;
    name: string;
    document: string;
}

export interface CommodityDelivery {
    id?: number;
    culture: CommodityCulture;
    harvest: string;
    bags?: number | null;
    weightKg?: number | null;
    grade?: string | null;
}

export interface CommodityVerification {
    id?: number;
    verificationType: VerificationType;
    scheduledDate?: string | null;
    verifiedDate?: string | null;
    responsible?: string | null;
    status: VerificationStatus;
    notes?: string | null;
}

export interface CommodityCustody {
    address: string | null;
    city: string | null;
    state: string | null;
}

export interface CommodityObligation {
    creditOperationId: string;
    allocatedPercentage: number | null;
    status: string;
    statusFormatted?: string;
}

export interface CommodityListItem {
    id: string;
    name: string;
    description: string | null;
    farmName: string | null;
    custodyCity: string | null;
    custodyState: string | null;
    legalRegimes: LegalRegime[];
    legalRegimesFormatted: string[];
    cultures: string[];
    culturesFormatted: string[];
    harvests: string[];
    deliveriesCount: number;
    archived: boolean;
    discharged: boolean;
    statusFormatted: string;
    createdAt: string;
    updatedAt: string;
}

export interface CommodityDetail {
    id: string;
    name: string;
    description: string | null;
    legalRegimes: { value: LegalRegime; formatted: string }[];
    custody: CommodityCustody;
    farmName: string | null;
    plantedArea: number | null;
    areaUnit: AreaUnit | null;
    areaUnitFormatted: string;
    areaOwner: string | null;
    leasedArea: boolean;
    registrationNumber: string | null;
    registryOffice: string | null;
    registryState: string | null;
    warehouseNotified: boolean;
    deliveries: CommodityDelivery[];
    verifications: CommodityVerification[];
    owners: CommodityOwner[];
    obligations: CommodityObligation[];
    archived: boolean;
    comments: string | null;
    discharged: boolean;
    dischargeDate: string | null;
    dischargeValue: number | null;
    statusFormatted: string;
    createdAt: string;
    updatedAt: string;
}

export const COMMODITY_CULTURE_OPTIONS = [
    { value: CommodityCulture.SOYBEAN, label: 'Soja' },
    { value: CommodityCulture.CORN, label: 'Milho' },
    { value: CommodityCulture.SORGHUM, label: 'Sorgo' },
    { value: CommodityCulture.COTTON, label: 'Algodão' },
    { value: CommodityCulture.WHEAT, label: 'Trigo' },
    { value: CommodityCulture.COFFEE, label: 'Café' },
    { value: CommodityCulture.OTHER, label: 'Outro' },
];

export const LEGAL_REGIME_OPTIONS = [
    { value: LegalRegime.AF, label: 'Alienação Fiduciária' },
    { value: LegalRegime.PLEDGE, label: 'Penhor' },
    { value: LegalRegime.MORTGAGE, label: 'Hipoteca' },
    { value: LegalRegime.FIDUCIARY_ASSIGNMENT, label: 'Cessão Fiduciária' },
];

export const VERIFICATION_TYPE_OPTIONS = [
    { value: VerificationType.PLANTING_1, label: 'Plantio 1' },
    { value: VerificationType.PLANTING_2, label: 'Plantio 2' },
    { value: VerificationType.HARVEST_SATELLITE, label: 'Colheita (satélite)' },
    { value: VerificationType.HARVEST_IN_LOCO, label: 'Colheita (in loco)' },
];

export const VERIFICATION_STATUS_OPTIONS = [
    { value: VerificationStatus.PENDING, label: 'Pendente' },
    { value: VerificationStatus.DONE, label: 'Concluído' },
    { value: VerificationStatus.SKIPPED, label: 'Dispensado' },
];

export const AREA_UNIT_OPTIONS = [
    { value: AreaUnit.HECTARE, label: 'Hectares' },
    { value: AreaUnit.SQUARE_METER, label: 'm²' },
];

export const MAX_OWNERS = 20;
